import { useT } from '@/i18n/useT';
import { useDeveloperStore } from '@/store/developerStore';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useUiStore, type PanelId } from '@/store/uiStore';
import { Divider } from '@/ui/components/Divider';
import { IconButton } from '@/ui/components/IconButton';
import { StatusIndicator } from '@/ui/components/StatusIndicator';
import styles from './StatusBar.module.css';

const PANEL_TOGGLES = [
  { panel: 'left', icon: 'panel-left', labelKey: 'status.panel.left' },
  { panel: 'right', icon: 'panel-right', labelKey: 'status.panel.right' },
  { panel: 'bottom', icon: 'panel-bottom', labelKey: 'status.panel.bottom' },
] as const satisfies readonly { panel: PanelId; icon: string; labelKey: string }[];

export function StatusBar() {
  const t = useT();
  const unavailable = t('status.unavailable');

  const projectName = useProjectStore((state) => state.project?.metadata?.name ?? '');
  const runState = useSimulationStore((state) => state.snapshot.state);
  const frameStats = useDeveloperStore((state) => state.frameStats);
  const developerVisible = useDeveloperStore((state) => state.visible);
  const toggleDeveloper = useDeveloperStore((state) => state.toggle);

  const left = useUiStore((state) => state.leftPanelVisible);
  const right = useUiStore((state) => state.rightPanelVisible);
  const bottom = useUiStore((state) => state.bottomPanelVisible);
  const togglePanel = useUiStore((state) => state.togglePanel);
  const visible = { left, right, bottom } satisfies Record<PanelId, boolean>;

  const items: readonly { id: string; label: string; value: string }[] = [
    { id: 'cpu', label: t('status.cpu'), value: unavailable },
    {
      id: 'fps',
      label: t('status.fps'),
      value: frameStats ? String(Math.round(frameStats.fps)) : unavailable,
    },
    { id: 'simulation', label: t('status.simulation'), value: runState },
    { id: 'plc', label: t('status.plc'), value: t('status.plc.notRunning') },
    {
      id: 'project',
      label: t('status.project'),
      value: projectName === '' ? t('app.untitledProject') : projectName,
    },
  ];

  return (
    <footer aria-label={t('status.label')} className={styles.statusBar}>
      <StatusIndicator tone="success" label={t('status.ready')} />
      <Divider />
      <ul className={styles.items}>
        {items.map((item) => (
          <li key={item.id} className={styles.item}>
            <span className={styles.itemLabel}>{item.label}:</span>
            <span className={styles.itemValue}>{item.value}</span>
          </li>
        ))}
      </ul>

      <div role="group" aria-label={t('status.panels.label')} className={styles.toggles}>
        {PANEL_TOGGLES.map(({ panel, icon, labelKey }) => (
          <IconButton
            key={panel}
            icon={icon}
            iconSize={18}
            label={t(labelKey)}
            placement="top-end"
            pressed={visible[panel]}
            className={styles.toggleButton}
            onClick={() => togglePanel(panel)}
          />
        ))}
        <IconButton
          icon="developer"
          iconSize={18}
          label={t('status.panel.developer')}
          placement="top-end"
          pressed={developerVisible}
          className={styles.toggleButton}
          onClick={toggleDeveloper}
        />
      </div>
    </footer>
  );
}
